import { useState, useEffect, useContext } from 'react';
import { InitialRenderTime } from './initialRender';
import { isPolishHoliday, skipHolidays } from '@/lib/polishHolidays';
import { SAME_DAY_ORDER_CUTOFF } from '@workspace/url-source/checkout';

/* ─── Warsaw time with second precision ─────────────────────── */
let warsawFormatter: Intl.DateTimeFormat | undefined;

export function warsawDetailed(now = new Date()) {
  warsawFormatter ??= new Intl.DateTimeFormat('pl-PL', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });
  const parts = warsawFormatter.formatToParts(now);
  const get = (t: string) => parseInt(parts.find(p => p.type === t)?.value ?? '0', 10);
  const year = get('year'), month = get('month'), day = get('day');
  const dateISO = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return {
    dateISO,
    month,
    day,
    dayOfWeek: new Date(year, month - 1, day).getDay(),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
  };
}

export function addDaysISO(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d + days);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

export function polishDateLabel(deliveryISO: string, todayISO: string): string {
  const [ty, tm, td] = todayISO.split('-').map(Number);
  const [dy, dm, dd] = deliveryISO.split('-').map(Number);
  const diffDays = Math.round(
    (new Date(dy, dm - 1, dd).getTime() - new Date(ty, tm - 1, td).getTime()) / 86_400_000,
  );
  if (diffDays === 1) return 'jutro';
  const names = ['w niedzielę', 'w poniedziałek', 'we wtorek', 'w środę', 'w czwartek', 'w piątek', 'w sobotę'];
  return names[new Date(dy, dm - 1, dd).getDay()];
}

export function firstSlotForDate(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y, m - 1, d).getDay() === 0 ? '10:00–13:00' : '9:00–11:00';
}

export function formatDateDDMM(isoDate: string): string {
  const [, m, d] = isoDate.split('-').map(Number);
  return `${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}`;
}

export function firstSlotStartHour(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y, m - 1, d).getDay() === 0 ? '10:00' : '9:00';
}

/* ─── Banner state ──────────────────────────────────────────── */
export type BannerState =
  | { kind: 'countdown'; secondsLeft: number; slotLabel: string }
  | { kind: 'next'; dateLabel: string; slotLabel: string; dateDDMM: string; startHour: string };

export function getBannerState(now?: Date): BannerState {
  const { dateISO, dayOfWeek, hour, minute, second } = warsawDetailed(now);
  const elapsed = hour * 3600 + minute * 60 + second;

  const [ty, tm, td] = dateISO.split('-').map(Number);
  if (isPolishHoliday(new Date(ty, tm - 1, td))) {
    const next = skipHolidays(addDaysISO(dateISO, 1));
    return { kind: 'next', dateLabel: polishDateLabel(next, dateISO), slotLabel: firstSlotForDate(next), dateDDMM: formatDateDDMM(next), startHour: firstSlotStartHour(next) };
  }
  const cutoff = dayOfWeek === 0 ? SAME_DAY_ORDER_CUTOFF.sunday : SAME_DAY_ORDER_CUTOFF.weekday;
  if (hour < cutoff) return { kind: 'countdown', secondsLeft: cutoff * 3600 - elapsed, slotLabel: '' };
  const next = skipHolidays(addDaysISO(dateISO, 1));
  return { kind: 'next', dateLabel: polishDateLabel(next, dateISO), slotLabel: firstSlotForDate(next), dateDDMM: formatDateDDMM(next), startHour: firstSlotStartHour(next) };
}

function sameBannerState(previous: BannerState, next: BannerState): boolean {
  if (previous.kind === 'countdown' && next.kind === 'countdown') {
    return previous.secondsLeft === next.secondsLeft && previous.slotLabel === next.slotLabel;
  }
  if (previous.kind === 'next' && next.kind === 'next') {
    return previous.dateLabel === next.dateLabel && previous.slotLabel === next.slotLabel
      && previous.dateDDMM === next.dateDDMM && previous.startHour === next.startHour;
  }
  return false;
}

/** Checks every second, updating React only when displayed delivery details change. */
export function useDeliveryCountdown() {
  const initialTime = useContext(InitialRenderTime);
  const [bannerState, setBannerState] = useState<BannerState>(() => getBannerState(initialTime === undefined ? undefined : new Date(initialTime)));

  useEffect(() => {
    const update = () => {
      const next = getBannerState();
      setBannerState(previous => sameBannerState(previous, next) ? previous : next);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, []);

  const isCountdown = bannerState.kind === 'countdown';
  let hh = '00', mm = '00', ss = '00';
  if (isCountdown) {
    const secs = Math.max(0, (bannerState as Extract<BannerState, { kind: 'countdown' }>).secondsLeft);
    hh = String(Math.floor(secs / 3600)).padStart(2, '0');
    mm = String(Math.floor((secs % 3600) / 60)).padStart(2, '0');
    ss = String(secs % 60).padStart(2, '0');
  }

  return { bannerState, isCountdown, hh, mm, ss };
}
